/*
Copyright 2026 WIN SOLUTIONS

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger } from "matrix-js-sdk/lib/logger";

import { type CallTransportMode } from "../settings/settings";

/**
 * Выбор вида связи для медиапотока звонка.
 *
 * Медиа идёт через ретранслятор (`iceTransportPolicy: "relay"` в connectLiveKitOnce),
 * а ретранслятор доступен двумя путями: UDP и TLS поверх TCP. Какой из них возьмёт
 * браузер, решает согласование ICE, и оно предпочитает UDP, пока тот хоть как-то
 * отвечает. Через туннель UDP отвечает наполовину: замер с машины CTO 31.08 — 30%
 * потерь и лучший пакет 786 мс, при том что TCP до того же сервера отвечал за 1–4 мс.
 * Согласование этого не замечает и остаётся на UDP, а человек видит рассыпающуюся
 * картинку и обрывы.
 *
 * Список ретрансляторов приходит от сервера вместе с паролями, и подменить его целиком
 * нельзя — паролей у нас нет. Поэтому мы не подменяем, а **отсеиваем**: перехватываем
 * создание соединения и оставляем в списке только те адреса, что отвечают выбранному
 * виду связи. Пароли остаются серверные, меняется только путь.
 */

let installed = false;
let currentMode: CallTransportMode = "auto";

/**
 * Переход на TLS при «Автоматически».
 *
 * Согласование ICE предпочитает UDP и, если тот отвечает наполовину, само на TCP не
 * уходит — проверено 18.09.2026 живым звонком CTO: пока UDP был открыт, пара кандидатов
 * скакала каждые 25 секунд; на пути поверх TLS перескоков ноль. Поэтому первую попытку
 * оставляем как есть, а если соединение не встало — вторую ведём только поверх TLS.
 *
 * Держится до конца работы вкладки: возвращаться на UDP внутри одного сеанса незачем,
 * туннель за это время обычно не меняется, а лишний возврат — это ещё один обрыв.
 */
let autoTcpFallback = false;

/** Вид связи, который применяется прямо сейчас, с учётом перехода на TLS. */
export function effectiveCallTransportMode(): CallTransportMode {
  return currentMode === "auto" && autoTcpFallback ? "tcp" : currentMode;
}

/** Включает путь поверх TLS для следующих попыток. Возвращает false, если он уже включён или не нужен. */
export function enableAutoTcpFallback(): boolean {
  if (currentMode !== "auto" || autoTcpFallback) return false;
  autoTcpFallback = true;
  installIceTransportFilter();
  logger.info("Соединение не встало — следующая попытка только поверх TLS");
  return true;
}

export function isAutoTcpFallbackActive(): boolean {
  return autoTcpFallback;
}

/** Только для тестов и для нового звонка с чистого листа. */
export function resetAutoTcpFallback(): void {
  autoTcpFallback = false;
}

/** Адрес ретранслятора работает поверх TCP (включая TLS)? */
function isTcpUrl(url: string): boolean {
  // turns: — это TURN поверх TLS, то есть всегда TCP.
  if (url.startsWith("turns:")) return true;
  return /[?&]transport=tcp\b/i.test(url);
}

/** Адрес ретранслятора работает поверх UDP? */
function isUdpUrl(url: string): boolean {
  if (url.startsWith("turns:")) return false;
  if (/[?&]transport=tcp\b/i.test(url)) return false;
  // Без указания вида связи TURN по умолчанию работает поверх UDP.
  return url.startsWith("turn:") || url.startsWith("stun:");
}

function keepUrl(url: string, mode: CallTransportMode): boolean {
  if (mode === "tcp") return isTcpUrl(url);
  if (mode === "udp") return isUdpUrl(url);
  return true;
}

/** Оставляет в списке ретрансляторов только подходящие выбранному виду связи. */
export function filterIceServers(
  servers: RTCIceServer[] | undefined,
  mode: CallTransportMode,
): RTCIceServer[] | undefined {
  if (!servers || mode === "auto") return servers;

  const filtered: RTCIceServer[] = [];
  for (const server of servers) {
    const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
    const kept = urls.filter(
      (url) => typeof url === "string" && keepUrl(url, mode),
    );
    if (!kept.length) continue;
    // Ничего не отсеяли — отдаём запись как была, вместе с исходным видом поля
    // (у RTCIceServer адрес бывает и строкой, и списком).
    filtered.push(
      kept.length === urls.length ? server : { ...server, urls: kept },
    );
  }

  // Пустой список означал бы звонок вообще без ретранслятора — это хуже, чем неверный
  // вид связи. Если после отсева не осталось ничего, возвращаем исходный список.
  if (!filtered.length) {
    logger.warn(
      `Вид связи «${mode}» выбран, но подходящих ретрансляторов сервер не предложил — оставляем как есть`,
    );
    return servers;
  }
  return filtered;
}

/**
 * Ставит перехват один раз за жизнь страницы.
 *
 * Перехватываем именно создание RTCPeerConnection, а не настройки LiveKit: список
 * ретрансляторов приходит внутри ответа сервера и до этой точки не виден.
 */
export function installIceTransportFilter(): void {
  if (installed) return;
  if (typeof window === "undefined" || !window.RTCPeerConnection) return;

  const Original = window.RTCPeerConnection;

  function Patched(
    this: unknown,
    config?: RTCConfiguration,
    constraints?: unknown,
  ): RTCPeerConnection {
    const mode = effectiveCallTransportMode();
    if (config && mode !== "auto") {
      const before = config.iceServers?.length ?? 0;
      config = {
        ...config,
        iceServers: filterIceServers(config.iceServers, mode),
      };
      logger.info(
        `Вид связи звонка: ${mode}${
          mode !== currentMode ? " (переход после неудачной попытки)" : ""
        }; ретрансляторов было ${before}, осталось ${config.iceServers?.length ?? 0}`,
      );
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new (Original as any)(config, constraints);
  }

  Patched.prototype = Original.prototype;
  Object.setPrototypeOf(Patched, Original);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.RTCPeerConnection = Patched as any;
  installed = true;
}

/** Меняет вид связи. Новое значение действует со следующего входа в звонок. */
export function setCallTransportMode(mode: CallTransportMode): void {
  if (mode !== currentMode) autoTcpFallback = false;
  currentMode = mode;
  installIceTransportFilter();
}

/** Только для тестов */
export function currentCallTransportMode(): CallTransportMode {
  return currentMode;
}
